<template>
  <div class="page-container">
    <div class="list-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <PageHeader :config="config" @module-click="handleModuleClick" />
      <Breadcrumb :items="breadcrumbItems" />

      <SearchBanner
        :title-main1="headerConfig.titleMain1"
        :title-main2="headerConfig.titleMain2"
        :subtitle="headerConfig.titleSub"
        :show-search="false"
        :background-image="bannerBg"
      />

      <div class="function-entries content-wrap">
        <div
          v-for="entry in functionEntries"
          :key="entry.id"
          class="entry-item"
          :class="'entry-item--' + entry.key"
          :style="entryCardStyle"
          @click="handleEntryClick(entry)"
        >
          <div class="entry-content">
            <div class="entry-title">{{ entry.title }}</div>
            <p class="entry-subtitle">{{ entry.subtitle }}</p>
          </div>
          <img
            v-if="entry.icon"
            class="entry-icon"
            :src="getSafeImage(entry.icon)"
            alt=""
            @error="onImgError"
          />
        </div>
      </div>

      <div class="content-area content-wrap">
        <div class="news-columns">
          <div class="news-col">
            <div class="news-col-head">
              <HomeSectionTitle
                :title="techPolicySection.title"
                :subtitle="techPolicySection.subtitle"
              />
              <span class="sec-more" @click="handleMoreClick('techPolicy')">
                {{ techPolicySection.moreText }}
              </span>
            </div>
            <div v-if="loading" class="section-empty section-empty--sm">{{ loadingText }}</div>
            <div v-else-if="techPolicyList.length" class="news-list">
              <div
                v-for="(item, index) in techPolicyList"
                :key="'tech-' + (item.id || index)"
                class="news-item"
                :class="{ zebra: index % 2 === 1 }"
                @click="handleNewsClick(item, 'techPolicy')"
              >
                <span class="news-icon">+</span>
                <span class="news-title" :title="item.title">{{ item.title || '' }}</span>
                <div class="news-meta">
                  <span class="meta-date">{{ item.date || '' }}</span>
                </div>
              </div>
            </div>
            <div v-else class="section-empty section-empty--sm">{{ emptyListText }}</div>
          </div>

          <div class="news-col">
            <div class="news-col-head">
              <HomeSectionTitle
                :title="policyInterpretSection.title"
                :subtitle="policyInterpretSection.subtitle"
              />
              <span class="sec-more" @click="handleMoreClick('policyInterpret')">
                {{ policyInterpretSection.moreText }}
              </span>
            </div>
            <div v-if="loading" class="section-empty section-empty--sm">{{ loadingText }}</div>
            <div v-else-if="policyInterpretList.length" class="news-list">
              <div
                v-for="(item, index) in policyInterpretList"
                :key="'interpret-' + (item.id || index)"
                class="news-item"
                :class="{ zebra: index % 2 === 1 }"
                @click="handleNewsClick(item, 'policyInterpret')"
              >
                <span class="news-icon">+</span>
                <span class="news-title" :title="item.title">{{ item.title || '' }}</span>
                <div class="news-meta">
                  <span class="meta-date">{{ item.date || '' }}</span>
                </div>
              </div>
            </div>
            <div v-else class="section-empty section-empty--sm">{{ emptyListText }}</div>
          </div>
        </div>
      </div>

      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { getInnovationServiceNewConfig } from '@/config/innovationServiceNewConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import HomeSectionTitle from '@/components/HomeSectionTitle.vue'
import request from '@/utils/request'
import baseUrl from '@/utils/baseUrl'
import { getSafeImage, handleImgError } from '@/utils/imageFallback'
import { getText, getCurrentLocale } from '@/utils/i18nText'
import { normalizeNewsNavItem, setNewsNavList } from '@/utils/newsNav'

export default {
  name: 'InnovationServiceNew',
  components: {
    PageFooter,
    PageHeader,
    Breadcrumb,
    SearchBanner,
    HomeSectionTitle
  },
  data() {
    return {
      config: {},
      breadcrumbItems: [],
      techPolicyList: [],
      policyInterpretList: [],
      loading: false,
      pageSize: 12
    }
  },
  computed: {
    isZhLocale() {
      return getCurrentLocale() === 'zh-CN'
    },
    headerConfig() {
      return this.config.header || {}
    },
    functionEntries() {
      return this.config.functionEntries || []
    },
    techPolicySection() {
      return this.config.newsSections?.techPolicy || {}
    },
    policyInterpretSection() {
      return this.config.newsSections?.policyInterpret || {}
    },
    bannerBg() {
      return getSafeImage(this.headerConfig.bannerBg)
    },
    entryCardStyle() {
      const src = getSafeImage(this.config.entryCardBg)
      if (!src) return null
      return {
        backgroundImage: `url(${src})`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    },
    emptyListText() {
      return this.config.list?.emptyList || getText('common.empty') || '暂无数据'
    },
    loadingText() {
      return this.config.pagination?.loading || getText('common.loading') || '加载中...'
    }
  },
  created() {
    this.loadConfig()
    this.loadNotices()
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    getSafeImage,
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },
    getPageScaleEl() {
      return this.$el.querySelector('.list-page')
    },
    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },
    onImgError(e) {
      handleImgError(e)
    },
    loadConfig() {
      this.config = getInnovationServiceNewConfig(getCurrentLocale())
      const bc = this.config.breadcrumb || {}
      this.breadcrumbItems = [
        { label: bc.home || '', path: '/index' },
        { label: bc.current || '', path: '' }
      ]
    },
    formatDateFromMs(ms) {
      const t = Number(ms)
      if (!Number.isFinite(t) || t <= 0) return ''
      const d = new Date(t)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    mapNoticeRow(row, type) {
      return {
        id: row.id,
        title: row.title || '',
        date: this.formatDateFromMs(row.updateTime || row.createTime),
        content: row.content || '',
        source: row.source || '',
        type: Number(row.type || type)
      }
    },
    async fetchByType(type) {
      const res = await request({
        url: '/vportal/notice/open/list',
        method: 'get',
        baseURL: baseUrl.apiBaseAuth,
        headers: {
          'Content-Type': 'application/json'
        },
        params: {
          page: 0,
          pageSize: this.pageSize,
          type
        }
      })
      const result = res.data || {}
      if (result.status !== 0 || !result.data) {
        return { list: [], total: 0 }
      }
      const rows = Array.isArray(result.data.rows) ? result.data.rows : []
      const sorted = rows
        .filter(Boolean)
        .sort((a, b) => {
          const topA = Number(a.isTop || 0)
          const topB = Number(b.isTop || 0)
          if (topA !== topB) return topB - topA
          const uA = Number(a.updateTime || a.createTime || 0)
          const uB = Number(b.updateTime || b.createTime || 0)
          return uB - uA
        })
      return {
        list: sorted.map(row => this.mapNoticeRow(row, type)),
        total: Number(result.data.total) || sorted.length
      }
    },
    async loadNotices() {
      this.loading = true
      try {
        const techType = this.techPolicySection.type || 3
        const interpretType = this.policyInterpretSection.type || 4
        const [tech, interpret] = await Promise.all([
          this.fetchByType(techType),
          this.fetchByType(interpretType)
        ])
        this.techPolicyList = tech.list
        this.policyInterpretList = interpret.list
      } catch (e) {
        console.error('加载科创服务公告失败:', e)
        this.techPolicyList = []
        this.policyInterpretList = []
      } finally {
        this.loading = false
      }
    },
    handleModuleClick() {},
    handleEntryClick(entry) {
      if (entry?.path) this.$router.push(entry.path)
    },
    handleMoreClick(sectionKey) {
      const section = this.config.newsSections?.[sectionKey] || {}
      const type = section.type || 3
      const titleMap = {
        techPolicy: { title1: '科技', title2: '政策' },
        policyInterpret: { title1: '政策', title2: '解读' }
      }
      const title = titleMap[sectionKey] || titleMap.techPolicy
      this.$router.push({
        name: 'policyInfo',
        query: {
          type: String(type),
          title1: title.title1,
          title2: title.title2
        }
      })
    },
    handleNewsClick(item, sectionKey) {
      if (!item || !item.id) return
      const list = sectionKey === 'policyInterpret' ? this.policyInterpretList : this.techPolicyList
      setNewsNavList(list.map(normalizeNewsNavItem))
      const labels =
        sectionKey === 'policyInterpret'
          ? { parentLabel: '政策解读', currentLabel: '政策解读详情', title1: '政策', title2: '解读' }
          : { parentLabel: '科技政策', currentLabel: '科技政策详情', title1: '科技', title2: '政策' }
      this.$router.push({
        path: '/index/news-detail/' + encodeURIComponent(String(item.id)),
        query: {
          id: String(item.id),
          title: item.title || '',
          date: item.date || '',
          source: item.source || '',
          content: encodeURIComponent(item.content || ''),
          parentLabel: labels.parentLabel,
          parentPath: '/innovation-service',
          currentLabel: labels.currentLabel,
          title1: labels.title1,
          title2: labels.title2
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background: #fff;
}

.list-page {
  width: 1920px;
  min-height: 100vh;
  background: #fff;
  transform-origin: top left;
}

.content-wrap {
  width: 1700px;
  margin-left: 110px;
  margin-right: auto;
  box-sizing: border-box;
}

::v-deep .header-banner {
  margin-bottom: 0;
}

.function-entries {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  margin-top: 28px;
  margin-bottom: 28px;
}

.entry-item {
  position: relative;
  height: 148px;
  background-color: #fff;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  box-sizing: border-box;
  transition: transform 0.25s, box-shadow 0.25s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(160, 144, 60, 0.15);
  }
}

.entry-content {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 120px 0 28px;
  box-sizing: border-box;
}

.entry-title {
  font-size: 22px;
  font-weight: 700;
  color: #a0903c;
  line-height: 1.3;
  margin-bottom: 10px;
}

.entry-subtitle {
  margin: 0;
  font-family: Arial, sans-serif;
  font-size: 12px;
  color: #aa9a4c;
  letter-spacing: 0.5px;
  line-height: 1.3;
}

.entry-icon {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  width: 88px;
  height: 88px;
  object-fit: contain;
  z-index: 1;
  pointer-events: none;
}

.entry-item--instrument .entry-icon {
  width: 70px;
  height: 90px;
}

.entry-item--org .entry-icon {
  width: 82px;
  height: 82px;
}

.entry-item--product .entry-icon {
  width: 92px;
  height: 80px;
}

.content-area {
  margin-bottom: 40px;
}

.news-columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.news-col {
  min-width: 0;
  background: #fff;
  border-radius: 4px;
  padding: 20px 22px 16px;
  box-sizing: border-box;
}

.news-col-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.sec-more {
  flex-shrink: 0;
  margin-top: 4px;
  font-size: 14px;
  color: #a0903c;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
}

.news-list {
  display: flex;
  flex-direction: column;
}

.news-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 10px 12px;
  box-sizing: border-box;
  cursor: pointer;
  background: #fff;

  &.zebra {
    background: #faf8f2;
  }

  &:hover .news-title {
    color: #8c7b21;
  }
}

.news-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 2px;
  background: #c4b056;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.news-title {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.news-meta {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 13px;
  color: #999;
}

.section-empty {
  width: 100%;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 16px;
}

.section-empty--sm {
  min-height: 180px;
  font-size: 14px;
}

.locale-zh-CN {
  .filter-label,
  .field-label {
    font-size: 16px;
  }

  .filter-chip,
  .field-chip {
    font-size: 18px;
    height: 36px;
    line-height: 34px;
    padding: 0 20px;
  }

  .entry-title {
    font-size: 26px;
  }

  .entry-subtitle {
    font-size: 14px;
  }

  .entry-item--instrument .entry-icon {
    width: 100px;
    height: 100px;
  }

  .entry-item--org .entry-icon,
  .entry-item--product .entry-icon {
    width: 88px;
    height: 96px;
  }

  .news-col-head,
  .news-more {
    font-size: 16px;
  }

  .news-title {
    font-size: 17px;
  }

  .news-meta,
  .news-date {
    font-size: 14px;
  }

  .news-icon {
    width: 18px;
    height: 18px;
  }
}

</style>
